<template>
  <div class="settings">
    <div class="settings__inner">
      <header class="settings__head">
        <h1 class="settings__title">Settings</h1>
        <p class="settings__subtitle">
          Preferences for the battleplan studio. Saved to this browser.
        </p>
      </header>

      <StPanel v-if="account" title="Account">
        <div class="settings__account">
          <div class="settings__account-id">
            <img
              v-if="account.discordAvatar"
              class="settings__avatar"
              :src="account.discordAvatar"
              alt=""
            />
            <span v-else class="settings__avatar settings__avatar--placeholder">
              <font-awesome-icon :icon="['fas', 'circle']" />
            </span>
            <div class="settings__account-text">
              <span class="settings__account-name">{{ account.username }}</span>
              <span class="settings__account-email">{{ account.email }}</span>
            </div>
          </div>
          <div class="settings__account-tags">
            <span v-if="account.isAdmin" class="settings__tag">Admin</span>
            <span v-else-if="account.studioAccess" class="settings__tag">Studio access</span>
            <span v-if="account.viaDiscord" class="settings__tag settings__tag--muted">
              via Discord
            </span>
          </div>
        </div>
        <StButton variant="secondary" icon="right-from-bracket" @click="signOut">
          Sign out
        </StButton>
      </StPanel>

      <StPanel title="Battleplan Editing">
        <StField
          label="Auto-select"
          inline
          hint="Select an element on the map right after you place it."
        >
          <StCheckbox v-model="settings.autoSelectPlaced">
            Auto-select newly placed elements
          </StCheckbox>
        </StField>
        <div class="settings__row">
          <StField
            label="Shared mirror"
            inline
            hint="Use one mirror default across the objective and terrain tools."
          >
            <StCheckbox v-model="settings.toolsShareMirror">
              Tools share mirror setting
            </StCheckbox>
          </StField>
        </div>
      </StPanel>

      <StPanel title="Authoring">
        <StField
          label="Show ids"
          inline
          hint="Reveal each entity's id in the forms, with controls to edit or regenerate it."
        >
          <StCheckbox v-model="settings.showIds"> Show and edit entity ids </StCheckbox>
        </StField>
      </StPanel>

      <StPanel title="Library">
        <p class="settings__note">
          {{ entryCount }} pack{{ entryCount === 1 ? '' : 's' }} stored locally. Packs and their
          battleplans live in this browser's storage; export to keep a copy.
        </p>
        <StButton variant="danger" icon="trash" :disabled="entryCount === 0" @click="clearLibrary">
          Clear local library
        </StButton>
      </StPanel>

      <StPanel title="Suggestions">
        <p class="settings__note">
          Values you've typed are remembered and offered as you type. Remove any you don't want
          suggested again.
        </p>
        <div v-for="cat in suggestionCategories" :key="cat.key" class="settings__suggest">
          <div class="settings__suggest-head">
            <span class="settings__suggest-label">
              {{ cat.label }} ({{ cat.store.suggestions.value.length }})
            </span>
            <StButton
              size="sm"
              variant="danger"
              icon="trash"
              :disabled="cat.store.suggestions.value.length === 0"
              @click="clearCategory(cat)"
            >
              Clear all
            </StButton>
          </div>
          <ul v-if="cat.store.suggestions.value.length" class="settings__suggest-list">
            <li v-for="s in cat.store.suggestions.value" :key="s" class="settings__suggest-chip">
              <span class="settings__suggest-chip-text">{{ s }}</span>
              <StIconButton
                icon="xmark"
                :label="`Remove ${s}`"
                size="sm"
                variant="danger"
                @click="cat.store.forget(s)"
              />
            </li>
          </ul>
          <p v-else class="settings__suggest-empty">None remembered yet.</p>
        </div>
      </StPanel>

      <StPanel title="About">
        <dl class="settings__about">
          <dt>Application</dt>
          <dd>Sigdex Studio</dd>
          <dt>Version</dt>
          <dd class="settings__mono">{{ version }}</dd>
        </dl>
      </StPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { StPanel, StField, StButton, StCheckbox, StIconButton } from '../components/ui/index.js';
import { client, claims } from '../client.js';
import { useSettings } from '../state/settings.js';
import { useLibrary } from '../state/library.js';
import { useTimingSuggestions } from '../composables/useTimingSuggestions.js';
import { useKeywordSuggestions } from '../composables/useKeywordSuggestions.js';
import type { SuggestionStore } from '../composables/useSuggestionStore.js';

declare const __PKG_VERSION__: string;

const settings = useSettings();
const lib = useLibrary();

const version = typeof __PKG_VERSION__ === 'string' ? __PKG_VERSION__ : 'dev';
const entryCount = computed(() => lib.entries.value.length);

// Present only when a user is signed in (prod gate); absent under `pnpm dev`.
const account = computed(() => {
  const c = claims.value;
  if (!c) return null;
  return {
    username: c.username,
    email: c.email,
    isAdmin: c.isAdmin,
    studioAccess: c.studioAccess,
    discordAvatar: c.discordAvatar ?? null,
    viaDiscord: c.loginMethod === 'discord',
  };
});

function signOut() {
  client.logout();
}

interface SuggestionCategory {
  key: string;
  label: string;
  store: SuggestionStore;
}
const suggestionCategories: SuggestionCategory[] = [
  { key: 'timings', label: 'Ability timings', store: useTimingSuggestions() },
  { key: 'ability', label: 'Ability keywords', store: useKeywordSuggestions('ability') },
  { key: 'unit', label: 'Unit keywords', store: useKeywordSuggestions('unit') },
  { key: 'weapon', label: 'Weapon abilities', store: useKeywordSuggestions('weapon') },
  { key: 'army', label: 'Army names', store: useKeywordSuggestions('army') },
  { key: 'enhancement', label: 'Enhancement tables', store: useKeywordSuggestions('enhancement') },
];

function clearCategory(cat: SuggestionCategory) {
  if (cat.store.suggestions.value.length === 0) return;
  if (window.confirm(`Remove all remembered ${cat.label.toLowerCase()}?`)) cat.store.clear();
}

function clearLibrary() {
  if (entryCount.value === 0) return;
  if (window.confirm('Delete ALL locally stored packs? This cannot be undone.')) {
    for (const entry of [...lib.entries.value]) lib.deletePack(entry.id);
  }
}
</script>

<style scoped>
.settings {
  height: 100%;
  overflow-y: auto;
  background: var(--st-canvas);
}
.settings__inner {
  max-width: 640px;
  margin: 0 auto;
  padding: var(--st-space-6) var(--st-space-5);
  display: flex;
  flex-direction: column;
  gap: var(--st-space-5);
}
.settings__head {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-1);
}
.settings__title {
  font-size: var(--st-text-xl);
  font-weight: var(--st-weight-bold);
  color: var(--st-text);
}
.settings__subtitle {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
}
.settings__row {
  margin-top: var(--st-space-4);
}
.settings__account {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-3);
  margin-bottom: var(--st-space-4);
}
.settings__account-id {
  display: flex;
  align-items: center;
  gap: var(--st-space-3);
  min-width: 0;
}
.settings__avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--st-radius-full);
  object-fit: cover;
  flex-shrink: 0;
}
.settings__avatar--placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--st-surface-sunken);
  color: var(--st-text-subtle);
}
.settings__account-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.settings__account-name {
  font-size: var(--st-text-md);
  font-weight: var(--st-weight-semibold);
  color: var(--st-text);
}
.settings__account-email {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.settings__account-tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-1);
}
.settings__tag {
  padding: 2px var(--st-space-2);
  background: var(--st-accent-tint);
  color: var(--st-accent-active);
  border-radius: var(--st-radius-full);
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-semibold);
}
.settings__tag--muted {
  background: var(--st-surface-sunken);
  color: var(--st-text-subtle);
}
.settings__note {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  margin-bottom: var(--st-space-4);
}
.settings__suggest + .settings__suggest {
  margin-top: var(--st-space-4);
}
.settings__suggest-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-2);
  margin-bottom: var(--st-space-2);
}
.settings__suggest-label {
  font-size: var(--st-text-sm);
  font-weight: var(--st-weight-semibold);
  color: var(--st-text);
}
.settings__suggest-list {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-1);
}
.settings__suggest-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--st-space-1);
  padding: 2px var(--st-space-1) 2px var(--st-space-2);
  background: var(--st-surface-sunken);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-full);
  font-size: var(--st-text-sm);
  color: var(--st-text);
}
.settings__suggest-empty {
  font-size: var(--st-text-sm);
  color: var(--st-text-subtle);
}
.settings__about {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--st-space-2) var(--st-space-5);
  font-size: var(--st-text-sm);
}
.settings__about dt {
  color: var(--st-text-subtle);
}
.settings__about dd {
  color: var(--st-text);
}
.settings__mono {
  font-family: var(--st-font-mono);
}
</style>
